import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from './api';
import omit from '../utils/omit';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/sku-checker`;

export type SkuItem = {
  _id: string;
  sku: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

type GetSkuItemsPayload = {
  active?: boolean;
  page: number;
  limit: number;
};

type GetSkuItemsResponse = {
  count: number;
  items: SkuItem[];
};

type CreateSkuItemPayload = Omit<SkuItem, '_id' | 'createdAt' | 'updatedAt'>;

type EditSkuItemPayload = { _id: string } & CreateSkuItemPayload;

type BulkImportSkuItemsPayload = {
  skus: string[];
  active?: boolean;
};

type BulkImportSkuItemsResponse = {
  created: number;
  skipped: number;
  duplicates: string[];
};

export const apiSkuChecker = createApi({
  reducerPath: 'apiSkuChecker',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['skuItems'],
  endpoints: (build) => ({
    getSkuItems: build.query<GetSkuItemsResponse, GetSkuItemsPayload>({
      query: (params) => ({
        url: '/',
        method: 'GET',
        params
      }),
      providesTags: ['skuItems']
    }),
    createSkuItem: build.mutation<void, CreateSkuItemPayload>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body
      }),
      invalidatesTags: ['skuItems']
    }),
    editSkuItem: build.mutation<void, EditSkuItemPayload>({
      query: (body) => ({
        url: `/${body._id}`,
        method: 'PUT',
        body: omit(body, ['_id'])
      }),
      invalidatesTags: ['skuItems']
    }),
    deleteSkuItem: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['skuItems']
    }),
    bulkImportSkuItems: build.mutation<BulkImportSkuItemsResponse, BulkImportSkuItemsPayload>({
      query: (body) => ({
        url: '/bulk-import',
        method: 'POST',
        body
      }),
      invalidatesTags: ['skuItems']
    })
  })
});

export const {
  useGetSkuItemsQuery,
  useLazyGetSkuItemsQuery,
  useCreateSkuItemMutation,
  useEditSkuItemMutation,
  useDeleteSkuItemMutation,
  useBulkImportSkuItemsMutation
} = apiSkuChecker;
